import React from 'react';
import {
  Box, Button, Checkbox, Select, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RankBadge from './RankBadge';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import Pagination from '../userAnalytics/Pagination';
import { C, entityColor, btnBase } from '../../theme/adminTokens';
import { initialOf } from '../../utils/avatarColor';

// Rows-per-page choices offered here (the API accepts up to 200 per page).
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const fmtLastPlayed = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const headCellSx = {
  fontSize: 11,
  fontWeight: 700,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: `1px solid ${C.border}`,
  borderRight: `1px solid ${C.border}`,
  '&:last-of-type': { borderRight: 'none' },
  whiteSpace: 'nowrap',
  py: 1.5,
  px: 2,
};

const bodyCellSx = {
  fontSize: 13,
  color: '#1e293b',
  borderBottom: `1px solid ${C.border}`,
  borderRight: `1px solid ${C.border}`,
  '&:last-of-type': { borderRight: 'none' },
  py: 1.5,
  px: 2,
  verticalAlign: 'middle',
};

export default function ScoreboardTable({
  rows, totalCount, page, pageSize, onPageChange, onPageSizeChange, onDetailsClick,
  // Optional selection column, added for GameAnnouncementSection's eligible-players table
  // (reusing this table rather than forking a new one) — every other caller leaves these
  // unset and gets the exact same table as before.
  selectable = false, selectedIds = [], onToggleSelect, onToggleSelectAll,
}) {
  const rowsPerPageLabel = useLocalText('Rows per page');

  if (rows.length === 0) {
    return (
      <Typography variant="body2" role="status" sx={{ color: C.muted, textAlign: 'center', py: 4 }}>
        <LocalText>No scores recorded for this period yet.</LocalText>
      </Typography>
    );
  }

  const selectedSet = new Set(selectedIds);
  const allSelected = rows.length > 0 && rows.every((r) => selectedSet.has(r.userId));
  const someSelected = rows.some((r) => selectedSet.has(r.userId)) && !allSelected;

  // Pages come straight from the total: a last page that is exactly full does not create an empty page after it.
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <>
      <TableContainer sx={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: '8px' }}>
        <Table
          size="small"
          aria-label="Game scoreboard"
          sx={{ '& tbody tr:last-of-type td': { borderBottom: 'none' } }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
              {selectable && (
                <TableCell scope="col" sx={{ ...headCellSx, width: 40, px: 1 }} padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e) => onToggleSelectAll?.(e.target.checked, rows)}
                    inputProps={{ 'aria-label': 'Select all eligible players' }}
                  />
                </TableCell>
              )}
              <TableCell scope="col" sx={headCellSx} align="center"><LocalText>Rank</LocalText></TableCell>
              <TableCell scope="col" sx={headCellSx} align="center"><LocalText>Player</LocalText></TableCell>
              <TableCell scope="col" sx={headCellSx} align="center"><LocalText>Email Address</LocalText></TableCell>
              <TableCell scope="col" sx={headCellSx} align="center"><LocalText>Score</LocalText></TableCell>
              <TableCell scope="col" sx={headCellSx} align="center"><LocalText>Last Played</LocalText></TableCell>
              {onDetailsClick && <TableCell scope="col" sx={headCellSx} align="center"><LocalText>Action</LocalText></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow
                key={row.userId}
                hover
                selected={selectable && selectedSet.has(row.userId)}
                // Explicit hover color — stays in the same neutral gray family as the
                // header (not blue), but lighter than it (#f8fafc vs the header's
                // #f1f5f9), so a hovered row reads as "slightly off-white", distinctly
                // paler than the header band rather than a shade toward/matching it.
                sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
              >
                {selectable && (
                  <TableCell sx={{ ...bodyCellSx, px: 1 }} padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedSet.has(row.userId)}
                      onChange={(e) => onToggleSelect?.(row, e.target.checked)}
                      inputProps={{ 'aria-label': `Select ${row.username || row.userId}` }}
                    />
                  </TableCell>
                )}
                <TableCell sx={bodyCellSx}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <RankBadge rank={row.rank} />
                  </Box>
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Same initial-letter avatar convention as User Analytics
                        (UserDrilldownModal.jsx's UserAvatar) — a plain colored circle
                        with the first letter, not an icon. */}
                    <Box
                      sx={{
                        width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: entityColor(idx), color: '#ffffff', fontSize: 11.5, fontWeight: 700,
                      }}
                    >
                      {initialOf(row.username || row.email)}
                    </Box>
                    {row.username}
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx}>{row.email || '—'}</TableCell>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }} align="center">{row.score}</TableCell>
                <TableCell sx={bodyCellSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: C.muted }} />
                    {fmtLastPlayed(row.lastPlayedTime)}
                  </Box>
                </TableCell>
                {onDetailsClick && (
                  <TableCell sx={bodyCellSx} align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onDetailsClick(row)}
                      sx={{
                        ...btnBase,
                        fontSize: 11.5,
                        borderColor: C.border,
                        color: C.navy,
                        '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' },
                      }}
                    >
                      <LocalText>Details</LocalText>
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Rows per page, which page of how many, and Prev / page numbers / Next: Prev is disabled on the first page and
          Next on the last, so Next is only enabled while another page of records exists. */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="label" htmlFor="scoreboard-rows-per-page" sx={{ fontSize: 12, fontWeight: 600, color: C.muted }}>
            <LocalText>Rows per page:</LocalText>
          </Box>
          <Select
            native
            size="small"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            inputProps={{ id: 'scoreboard-rows-per-page', 'aria-label': rowsPerPageLabel }}
            sx={{
              bgcolor: '#ffffff', borderRadius: '8px', fontSize: 12.5, fontWeight: 600, color: C.navy,
              '& .MuiSelect-select': { py: '5px' }, '& fieldset': { borderColor: C.border },
            }}
          >
            {ROWS_PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </Select>
        </Box>
        <Typography data-testid="scoreboard-page" sx={{ fontSize: 12.5, fontWeight: 500, color: C.muted, textAlign: 'center' }}>
          <LocalText>Page</LocalText>{' '}
          <Box component="strong" sx={{ color: '#1e293b' }}>{page}</Box>{' '}
          <LocalText>of</LocalText>{' '}
          <Box component="strong" sx={{ color: '#1e293b' }}>{totalPages}</Box>
        </Typography>
        <Pagination page={page} totalPages={totalPages} onChange={onPageChange} />
      </Box>
    </>
  );
}
